/**
 * +personas: helpers around persona bots for the front ends (web session, Room DO, harness):
 * a default companion roster when none is supplied, and a summary of a run's social events.
 */
import type { Archetype, Persona } from '@keystone/content';
import { generatePersona } from '@keystone/content';
import type { BotBrain, ClassId, LeaveReason, Ms, Presence, SimEvent, UnitId } from '@keystone/sim';
import { createSeededRng } from '@keystone/sim';
import type { PersonaBrain } from './brain';

/** Archetypes a default companion roster draws from (no tryhard: they are on their best behaviour anyway). */
const COMPANION_ARCHETYPES: readonly Archetype[] = ['chill', 'leader', 'clown', 'goblin', 'distracted', 'newbie', 'silent', 'tryhard'];

/**
 * Three (one per class given) deterministic companions for a run seed, with distinct archetypes and
 * names. Used when no roster is supplied (web solo runs, party rooms). `taken`: names to avoid (the
 * human's hero name).
 */
export function defaultCompanions(classIds: readonly ClassId[], seed: number, taken: readonly string[] = []): Persona[] {
  const rng = createSeededRng((seed ^ 0x2c1b3c6d) >>> 0);
  const pool = [...COMPANION_ARCHETYPES];
  const names = new Set(taken);
  return classIds.map((classId, i) => {
    const archetype = pool.splice(rng.int(0, pool.length - 1), 1)[0] ?? 'chill';
    const p = generatePersona({ classId, seed: (Math.imul(seed >>> 0, 2654435761) + (i + 1) * 0x9e3779b9) >>> 0, archetype, taken: names });
    names.add(p.name);
    return p;
  });
}

export function isPersonaBrain(b: BotBrain): b is PersonaBrain {
  return 'persona' in b && 'incidents' in b;
}

/** What happened socially in a run, read from sim events alone (works on any client / replay). */
export interface SocialSummary {
  /** Chat lines per unit. */
  lines: Record<UnitId, number>;
  /** AFK spans (end null = never came back). */
  afk: { unitId: UnitId; from: Ms; to: Ms | null }[];
  wanders: { unitId: UnitId; from: Ms; to: Ms | null }[];
  /** Members who left, in order. */
  left: { unitId: UnitId; at: Ms; reason: LeaveReason; blameId?: UnitId; cause?: string }[];
  /** Total time members spent away (AFK + wandering), ms. */
  awayMs: Ms;
}

export function socialSummary(events: readonly SimEvent[], endMs?: Ms): SocialSummary {
  const out: SocialSummary = { lines: {}, afk: [], wanders: [], left: [], awayMs: 0 };
  const open = new Map<UnitId, { status: Presence; from: Ms }>();
  const close = (id: UnitId, at: Ms | null): void => {
    const o = open.get(id);
    if (!o) return;
    open.delete(id);
    (o.status === 'afk' ? out.afk : out.wanders).push({ unitId: id, from: o.from, to: at });
  };
  let last = 0;
  for (const e of events) {
    last = e.timestamp;
    if (e.type === 'chat') out.lines[e.sourceID] = (out.lines[e.sourceID] ?? 0) + 1;
    else if (e.type === 'presence') {
      close(e.sourceID, e.timestamp);
      if (e.status) open.set(e.sourceID, { status: e.status, from: e.timestamp });
    } else if (e.type === 'party_leave') {
      close(e.sourceID, null);
      out.left.push({ unitId: e.sourceID, at: e.timestamp, reason: e.reason, ...(e.blameId ? { blameId: e.blameId } : {}), ...(e.cause ? { cause: e.cause } : {}) });
    }
  }
  for (const id of [...open.keys()]) close(id, null);
  const end = endMs ?? last;
  for (const s of [...out.afk, ...out.wanders]) out.awayMs += (s.to ?? end) - s.from;
  return out;
}
